import { z } from "zod";

const allowedInternalPaths = new Set([
  "/workspace",
  "/chat-history",
  "/my-files",
  "/notifications",
  "/settings/account",
  "/settings/notifications",
  "/settings/billing",
  "/settings/support",
]);

function hasUnsafeTargetCharacters(value: string) {
  return (
    value.trim() !== value ||
    value.includes("\\") ||
    Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint < 32 || codePoint === 127;
    })
  );
}

export function isSafeExternalHelpUrl(value: string) {
  if (hasUnsafeTargetCharacters(value)) return false;

  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      parsed.hostname.length > 0 &&
      parsed.username === "" &&
      parsed.password === ""
    );
  } catch {
    return false;
  }
}

export function isSafeInternalHelpRoute(value: string) {
  if (
    hasUnsafeTargetCharacters(value) ||
    !value.startsWith("/") ||
    value.startsWith("//")
  ) {
    return false;
  }

  try {
    const base = "https://internal.merry.invalid";
    const parsed = new URL(value, base);
    return parsed.origin === base && allowedInternalPaths.has(parsed.pathname);
  } catch {
    return false;
  }
}

const externalActionWireSchema = z
  .object({
    kind: z.literal("external_url"),
    open_mode: z.enum(["same_tab", "new_tab"]),
    target: z.string().max(2048).refine(isSafeExternalHelpUrl),
  })
  .strict();

const internalActionWireSchema = z
  .object({
    kind: z.literal("internal_route"),
    open_mode: z.literal("same_tab"),
    target: z.string().max(2048).refine(isSafeInternalHelpRoute),
  })
  .strict();

const helpSupportEntryWireSchema = z
  .object({
    action: z.discriminatedUnion("kind", [
      externalActionWireSchema,
      internalActionWireSchema,
    ]),
    description: z.string().max(240),
    entry_key: z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/u),
    id: z.string().min(1),
    indicator_kind: z.enum(["none", "external"]),
    presentation: z.enum(["featured", "standard"]),
    revision: z.string().regex(/^[1-9]\d*$/u),
    title: z.string().min(1).max(60),
  })
  .strict();

export const helpSupportEntriesResponseSchema = z
  .object({
    entries: z.array(helpSupportEntryWireSchema),
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => ({
    entries: response.entries.map((entry) => ({
      action:
        entry.action.kind === "external_url"
          ? {
              kind: entry.action.kind,
              openMode: entry.action.open_mode,
              target: entry.action.target,
            }
          : {
              kind: entry.action.kind,
              openMode: entry.action.open_mode,
              target: entry.action.target,
            },
      description: entry.description,
      entryKey: entry.entry_key,
      id: entry.id,
      indicatorKind: entry.indicator_kind,
      presentation: entry.presentation,
      revision: entry.revision,
      title: entry.title,
    })),
    schemaVersion: response.schema_version,
  }));

export type HelpSupportEntriesResponse = ReturnType<
  typeof helpSupportEntriesResponseSchema.parse
>;
export type HelpSupportEntry = HelpSupportEntriesResponse["entries"][number];
